import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Send, RotateCcw } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import SectionLabel from "@/components/site/SectionLabel";
import Seo from "@/components/site/Seo";
import { submitContestantInterest } from "@/lib/api";
import { PROMISES } from "@/lib/constants";

const STEPS = [
  {
    n: "01",
    h: "Express Interest",
    p: "Share your name, message, and how to reach you. We confirm receipt and add you to the contestant list.",
  },
  {
    n: "02",
    h: "Application Window Opens",
    p: "When the full application opens, we invite you to submit your talk description, sample video, references, and agreements.",
  },
  {
    n: "03",
    h: "Selection & Preparation",
    p: "Selected contestants receive coaching guidance, schedule details, and stage preparation support before the live event.",
  },
];

const RECEIVE = [
  "A real, professionally produced stage",
  "Structured, honest judging",
  "Audience exposure across attendees, sponsors, and partners",
  "A professionally documented performance — captured in photo and video",
  "Coaching pointers and post-event feedback",
  "Connections to publishing, speaking, and business pathways",
];

const initial = {
  name: "",
  email: "",
  phone: "",
  city_state: "",
  message_summary: "",
  how_heard: "",
};

export default function Apply() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await submitContestantInterest(form);
      setSubmitted(true);
      toast.success("Thank you. Your interest has been received.");
      setForm(initial);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Submission failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Seo />
      <PageHero
        pageTitle="Apply To Speak"
        eyebrowNumber="01"
        eyebrow="Step Onto The Stage"
        title="Apply To"
        highlight="Speak"
        tagline="If you are serious about your message, we will give you a serious stage."
        description="Submit your interest below. When the full application opens, we'll invite you to complete the next step."
      />

      {/* PROCESS */}
      <section className="bg-midnight border-t border-slate-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
          <SectionLabel number="02">The Process</SectionLabel>
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {STEPS.map((s) => (
              <div key={s.n} className="card-dark p-8 relative">
                <div className="font-shoulders font-black text-7xl text-slate-soft absolute -top-2 right-4 select-none">
                  {s.n}
                </div>
                <h3 className="font-shoulders font-bold uppercase tracking-wider text-2xl text-white relative z-10">
                  {s.h}
                </h3>
                <div className="gold-line" />
                <p className="font-manrope text-soft-gray mt-4 leading-relaxed relative z-10">
                  {s.p}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU RECEIVE */}
      <section className="bg-navy border-t border-slate-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionLabel number="03">What Contestants Receive</SectionLabel>
            <h2 className="font-bebas text-white text-4xl sm:text-5xl leading-[0.95]">
              A Real Stage —<br />
              <span className="gold-text">And Real Substance.</span>
            </h2>
            <p className="font-cormorant text-2xl text-gold-bright/90 mt-6">
              “{PROMISES.contestants}”
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid sm:grid-cols-2 gap-4">
              {RECEIVE.map((r, i) => (
                <li key={r} className="card-dark p-5 flex items-start gap-4">
                  <span className="font-shoulders font-black text-gold text-2xl shrink-0">
                    0{i + 1}
                  </span>
                  <span className="font-manrope text-soft-gray text-base leading-relaxed">
                    {r}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* INTEREST FORM */}
      <section
        id="interest-form"
        className="bg-midnight border-t border-slate-soft"
      >
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-14">
          <SectionLabel number="04" align="center">
            Contestant Interest
          </SectionLabel>
          <h2 className="font-bebas text-white text-4xl sm:text-5xl leading-[0.95] text-center">
            Step <span className="gold-text">Forward.</span>
          </h2>
          <p className="font-manrope text-soft-gray text-center mt-4 max-w-xl mx-auto">
            Short and direct. We confirm receipt and follow up when the
            application window opens.
          </p>

          {submitted ? (
            <div
              data-testid="apply-thank-you"
              className="corner-bracket card-dark p-10 mt-8 text-center"
            >
              <div className="label-bebas text-gold">Received</div>
              <h3 className="font-bebas text-white text-3xl sm:text-4xl mt-2">
                Thank You For Stepping Forward.
              </h3>
              <p className="font-manrope text-soft-gray mt-4 max-w-md mx-auto">
                We've logged your interest. When the full application opens, we
                will email you directly.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                data-testid="apply-submit-another"
                className="btn-outline mt-8"
              >
                <RotateCcw size={18} strokeWidth={1.8} />
                Submit Another
              </button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="contestant-interest-form"
              className="mt-8 card-dark p-8 sm:p-10 space-y-6"
              noValidate
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="label-bebas" htmlFor="apply-name">
                    Full Name *
                  </label>
                  <input
                    id="apply-name"
                    name="name"
                    required
                    value={form.name}
                    onChange={onChange}
                    data-testid="apply-input-name"
                    className="input-dark"
                  />
                </div>
                <div>
                  <label className="label-bebas" htmlFor="apply-email">
                    Email *
                  </label>
                  <input
                    id="apply-email"
                    type="email"
                    name="email"
                    required
                    value={form.email}
                    onChange={onChange}
                    data-testid="apply-input-email"
                    className="input-dark"
                  />
                </div>
                <div>
                  <label className="label-bebas" htmlFor="apply-phone">
                    Phone
                  </label>
                  <input
                    id="apply-phone"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    data-testid="apply-input-phone"
                    className="input-dark"
                  />
                </div>
                <div>
                  <label className="label-bebas" htmlFor="apply-city">
                    City / State
                  </label>
                  <input
                    id="apply-city"
                    name="city_state"
                    value={form.city_state}
                    onChange={onChange}
                    data-testid="apply-input-city"
                    className="input-dark"
                  />
                </div>
              </div>
              <div>
                <label className="label-bebas" htmlFor="apply-message">
                  Your Message — One Sentence *
                </label>
                <textarea
                  id="apply-message"
                  name="message_summary"
                  required
                  rows={3}
                  value={form.message_summary}
                  onChange={onChange}
                  data-testid="apply-input-message"
                  className="input-dark"
                  placeholder="What is the message you want to bring to the stage?"
                />
              </div>
              <div>
                <label className="label-bebas" htmlFor="apply-how">
                  How Did You Hear About Us?
                </label>
                <input
                  id="apply-how"
                  name="how_heard"
                  value={form.how_heard}
                  onChange={onChange}
                  data-testid="apply-input-how"
                  className="input-dark"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="apply-submit-button"
                className="btn-gold w-full sm:w-auto"
              >
                <Send size={18} strokeWidth={1.8} />
                {submitting ? "Sending…" : "Submit Interest"}{" "}
                {!submitting && <ArrowRight size={16} />}
              </button>
              <p className="text-muted-ink text-xs font-manrope">
                By submitting, you agree to be contacted regarding The Next
                Stage. We do not sell or share your information.
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
